import { Asset } from '../../../../store/Asset';
import { ProvisionalAsset } from '../../../../store/ProvisionalAsset';
import { getDecimal } from '../../../../utils/helpers';

export const assetKeys = {
  commissionableMRC: 'commissionableMRC',
  totalMRC: 'totalMRC',
  recurringCommissionPercent: 'recurringCommissionPercent',
  grossMRR: 'grossMRR',
  commissionableNRC: 'commissionableNRC',
  nonRecurringCommissionPercent: 'nonRecurringCommissionPercent',
  nonCommissionableNRC: 'nonCommissionableNRC',
  grossNRR: 'grossNRR',
  netMRR: 'netMRR',
  netNRR: 'netNRR',
  status: 'commissionsStatus',
  actualFirstCommissionRun: 'actualFirstCommissionRun',
  actualFirstSpiffRun: 'actualFirstSpiffRun',
  actualDecommissionedDate: 'actualDecommissionedDate',
  expectedFirstCommissionRun: 'expectedFirstCommissionRun',
  expectedDecommissionDate: 'expectedDecommissionDate',
  serviceStatus: 'assetStatus'
};

export const provisionalAssetKeys = {
  commissionableMRC: 'commissionableMrc',
  totalMRC: 'mrc',
  recurringCommissionPercent: 'recurringCommission',
  grossMRR: 'expectedGrossMrr',
  commissionableNRC: 'commissionableNrc',
  nonRecurringCommissionPercent: 'nonRecurringCommission',
  nonCommissionableNRC: 'nonCommissionableNrc',
  grossNRR: 'expectedGrossNrr',
  netMRR: 'expectedNetMrr',
  netNRR: 'expectedNetNrr',
  status: 'commissionStatus',
  actualFirstCommissionRun: 'actualFirstCommissionRun',
  actualFirstSpiffRun: 'actualFirstSpiffRun',
  actualDecommissionedDate: 'actualDecommissionedDate',
  expectedFirstCommissionRun: undefined,
  expectedDecommissionDate: 'expectedDecommissionDate',
  serviceStatus: undefined
};

export const allKeys = {
  asset: assetKeys,
  provisionalAsset: provisionalAssetKeys
};

export interface FinancialFields {
  commissionableMRC: number | null;
  totalMRC: number | null;
  recurringCommissionPercent: number | null;
  grossMRR: number | null;
  commissionableNRC: number | null;
  nonRecurringCommissionPercent: number | null;
  nonCommissionableNRC: number | null;
  grossNRR: number | null;
  netMRR: number | null;
  netNRR: number | null;
}

export const FINANCIAL_FIELD_NAMES: (keyof FinancialFields)[] = [
  'commissionableMRC',
  'totalMRC',
  'recurringCommissionPercent',
  'grossMRR',
  'commissionableNRC',
  'nonRecurringCommissionPercent',
  'nonCommissionableNRC',
  'grossNRR',
  'netMRR',
  'netNRR'
];

export interface FormDataState extends FinancialFields {
  status: string | null;
  actualFirstCommissionRun: string | null;
  actualFirstSpiffRun: string | null;
  actualDecommissionedDate: string | null;
}

export const MRR_INPUT_NAMES: (keyof FormDataState)[] = ['commissionableMRC', 'recurringCommissionPercent'];
export const NRR_INPUT_NAMES: (keyof FormDataState)[] = ['commissionableNRC', 'nonRecurringCommissionPercent'];

export const CALCULATED_FIELD_INPUTS: Partial<Record<keyof FormDataState, (keyof FormDataState)[]>> = {
  grossMRR: MRR_INPUT_NAMES,
  netMRR: MRR_INPUT_NAMES,
  grossNRR: NRR_INPUT_NAMES,
  netNRR: NRR_INPUT_NAMES
};

export const REVENUE_CHANGE_CATEGORY_FIELDS: (keyof FormDataState)[] = FINANCIAL_FIELD_NAMES.filter(
  (field) => !(field in CALCULATED_FIELD_INPUTS)
);

export const isBlank = (candidate: unknown) => candidate === null || candidate === undefined || candidate === '';

export const DATE_FIELD_NAMES: (keyof FormDataState)[] = [
  'actualFirstCommissionRun',
  'actualFirstSpiffRun',
  'actualDecommissionedDate'
];

export const SERVER_ERROR_FIELDS: (keyof FormDataState)[] = [...REVENUE_CHANGE_CATEGORY_FIELDS, ...DATE_FIELD_NAMES];

export const toTimestamp = (isoDate: unknown) => new Date(String(isoDate)).getTime();

export const isSameValue = (field: keyof FormDataState, editedValue: unknown, storedValue: unknown) => {
  if (isBlank(editedValue) || isBlank(storedValue)) return isBlank(editedValue) && isBlank(storedValue);
  if (typeof editedValue === 'number' || typeof storedValue === 'number') {
    return Number(editedValue) === Number(storedValue);
  }
  if (DATE_FIELD_NAMES.includes(field)) return toTimestamp(editedValue) === toTimestamp(storedValue);
  return editedValue === storedValue;
};

type EntityUpdate<Entity extends { id: string }> = Partial<Entity> & Pick<Entity, 'id'>;

export type FinancialEditPayload = EntityUpdate<Asset> | EntityUpdate<ProvisionalAsset>;

type EditKeys = (typeof allKeys)[keyof typeof allKeys];

export const isFieldEdited = (field: keyof FormDataState, formData: FormDataState, initialFormData: FormDataState) =>
  !isSameValue(field, formData[field], initialFormData[field]);

export const getChangedFields = (
  formData: FormDataState,
  initialFormData: FormDataState,
  storedFinancialData: FinancialFields
) =>
  (Object.keys(formData) as (keyof FormDataState)[]).filter((field) => {
    const calculatedFrom = CALCULATED_FIELD_INPUTS[field];
    if (!calculatedFrom) return isFieldEdited(field, formData, initialFormData);
    return (
      calculatedFrom.some((input) => isFieldEdited(input, formData, initialFormData)) &&
      !isSameValue(field, formData[field], storedFinancialData[field as keyof FinancialFields])
    );
  });

export const getIdentityFields = (type: 'asset' | 'provisionalAsset', data: Asset | ProvisionalAsset) => {
  if (type === 'asset') return { id: data.id };
  const { id, accountId, salesAgent, supplier, tsd } = data as ProvisionalAsset;
  return { id, accountId, salesAgent, supplier, tsd };
};

interface SavePayloadInput {
  type: 'asset' | 'provisionalAsset';
  data: Asset | ProvisionalAsset;
  keys: EditKeys;
  formData: FormDataState;
  initialFormData: FormDataState;
  changedFields: (keyof FormDataState)[];
  revenueChangeCategory: string;
}

export const buildSavePayload = ({
  type,
  data,
  keys,
  formData,
  initialFormData,
  changedFields,
  revenueChangeCategory
}: SavePayloadInput): FinancialEditPayload => {
  const newValues: Record<string, unknown> = Object.fromEntries(
    changedFields.map((field) => [keys[field as keyof typeof keys], formData[field]])
  );
  if (type === 'asset' && MRR_INPUT_NAMES.some((input) => isFieldEdited(input, formData, initialFormData))) {
    const x2Rate = (data as Asset).x2Rate;
    const netRevenueSplit = x2Rate !== null && x2Rate !== undefined ? 100 - x2Rate : 100;
    newValues['netRevenue'] = getDecimal(Number(formData.grossMRR) * (netRevenueSplit / 100));
  }
  if (revenueChangeCategory) newValues['revenueChangeCategory'] = revenueChangeCategory;

  return { ...getIdentityFields(type, data), ...newValues } as FinancialEditPayload;
};

/** Parses `field message` / `field: message` parts joined by `, ` into per-field errors; the rest goes under `form`. */
export const toServerErrors = (message: string, keys: EditKeys): { [key: string]: string } => {
  const formFieldByServerKey: Record<string, string> = {
    revenueChangeCategory: 'revenueChangeCategory',
    ...Object.fromEntries(SERVER_ERROR_FIELDS.map((field) => [keys[field as keyof typeof keys], field]))
  };
  const serverKeys = Object.keys(formFieldByServerKey).join('|');
  const messageSeparator = new RegExp(`, (?=(?:${serverKeys})(?::|\\s))`);
  const fieldMessage = new RegExp(`^(${serverKeys})(?::\\s*|\\s+)([\\s\\S]*)$`);

  const serverErrors: { [key: string]: string } = {};
  const unmatchedMessages: string[] = [];
  message.split(messageSeparator).forEach((part) => {
    const match = part.match(fieldMessage);
    if (!match) return unmatchedMessages.push(part);
    const formField = formFieldByServerKey[match[1]];
    serverErrors[formField] = serverErrors[formField] ? `${serverErrors[formField]}, ${match[2]}` : match[2];
  });
  if (unmatchedMessages.length) serverErrors.form = unmatchedMessages.join(', ');
  return serverErrors;
};
